import { useState, useEffect, useRef } from 'react';
import { hasAValue, isEmptyObj } from 'Utils/Utils';
import { trackFormStarted, trackFormCompleted, trackFormError } from 'Utils/Analytics';

export const useForm = ({
  formName /* for analytics */,
  onFormSubmit,
  initialValues,
  pageStartSource,
  pagePlanFlowType,
  onFormStartedTrackingFields,
  onFormCompletedTrackingFields,
  alwaysAllowSubmit = false,
}) => {
  const [fieldValues, setFieldValues] = useState(initialValues || {});
  const [fieldErrors, setFieldErrors] = useState({});
  //could have multiple fields validating at once especially since have async validators
  const [validating, setValidating] = useState({});
  const [formError, setFormError] = useState({}); // { formErrorId: [Number], message: [String]} Id so is a new prop
  //override "touched" so field validation errors can show.
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('unsubmitted'); //'unsubmitted', 'success', 'fail'

  //to prevent potential React warning about updating component state after it has unmounted
  const isCancelled = useRef(false);

  useEffect(() => {
    return () => {
      isCancelled.current = true;
    };
  }, []);

  useEffect(() => {
    if (typeof onFormStartedTrackingFields === 'function') {
      const customTrackingFields = onFormStartedTrackingFields();
      trackFormStarted(formName, customTrackingFields);
    }
  }, [formName, onFormStartedTrackingFields]);

  const resetFormError = () => {
    setFormError({});
  };

  const updateValue = (fieldName, value) => {
    if (formError) resetFormError();

    setFieldValues((oldValues) => {
      return { ...oldValues, [fieldName]: value };
    });
  };

  const updateError = (fieldName, error) => {
    if (error) {
      trackFormError(
        formName,
        {
          status: 1000,
          message: error,
        },
        {
          page_start_source: pageStartSource || 'other',
          page_plan_flow_type: pagePlanFlowType || undefined,
        }
      );
    }
    setFieldErrors((oldErrors) => {
      return { ...oldErrors, [fieldName]: error };
    });
  };

  const updateValidating = (fieldName, isValidating) => {
    setValidating((oldValidating) => {
      return { ...oldValidating, [fieldName]: isValidating };
    });
  };

  //on mount, first field starts validating. This const prevents flicker of submit button being enabled before that
  const emptyValidating = isEmptyObj(validating);

  const submitDisabled = emptyValidating || hasAValue(validating) || hasAValue(fieldErrors);

  const handleSubmit = async (event) => {
    event.preventDefault();
    resetFormError();
    setFormSubmitted(true);

    if (alwaysAllowSubmit || !submitDisabled) {
      setSubmitting(true);
      const result = await onFormSubmit(fieldValues);
      if (!isCancelled.current) {
        setSubmitting(false);
        if (result && result.formErrorId) {
          setFormError(result);
          setSubmitStatus('fail');
        } else {
          setSubmitStatus('success');
        }
      }
      if (typeof onFormCompletedTrackingFields === 'function') {
        const customTrackingFields = onFormCompletedTrackingFields();
        trackFormCompleted(formName, customTrackingFields);
      }
    }
  };
  return {
    //for fields:
    formFieldProps: {
      fieldValues,
      fieldErrors,
      formSubmitted,
      updateValue,
      updateError,
      updateValidating,
    },
    //for form:
    submitting,
    submitDisabled,
    formError,
    setFormError,
    resetFormError,
    handleSubmit,
    submitStatus,
  };
};
